import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'

import Modal from 'modules/core/components/Modal'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/Select'
import i18n from 'modules/core/lib/i18n'

import CurrencySelector from 'modules/finance/components/CurrencySelector'
import { useSalary } from 'modules/job_catalog/components/SalaryModal/useSalary'
import { ValidationContext } from 'modules/job_catalog/components/SalaryModal/validations'
import { Salary, Workplace } from 'modules/job_catalog/lib/salary'

import { WorkplaceSelector } from './WorkplaceSelector'

interface Props {
  onClose: () => void
  onSubmit: () => void
  createAction: (salary: Salary, nodeUuid: string) => Promise<void>
  updateAction: (salary: Salary, nodeUuid: string) => Promise<void>
  name: string
  nodeType: string
  initialSalary?: Salary
  alreadySelectedWorkplaces: Workplace[]
  hasAllWorkplacesSalary: boolean
}

const SalaryModal = ({
  name,
  onClose,
  createAction,
  updateAction,
  onSubmit,
  initialSalary,
  alreadySelectedWorkplaces,
  hasAllWorkplacesSalary,
}: Props) => {
  const params = useParams()
  const { rootId, familyId, functionId, roleId, levelId, id } = params
  const nodeId = levelId || roleId || functionId || familyId || rootId || id || ''

  const [totalWorkplaces, setTotalWorkplaces] = useState(0)

  const title = i18n.t('job_catalog.nodes.salary_modal.title', { name })
  const description = i18n.t('job_catalog.nodes.salary_modal.description_workplaces')

  const alreadySelectedInOtherSalariesWorkplaces = alreadySelectedWorkplaces.filter(
    (workplace) => !initialSalary?.workplaces?.includes(workplace)
  )

  const validationContext: ValidationContext = {
    hasAllWorkplacesSalary,
    disabledWorkplaces: alreadySelectedInOtherSalariesWorkplaces,
    totalWorkplaces,
  }

  const {
    minAmount,
    maxAmount,
    grossSalary,
    currency,
    workplaces,
    periodicity,
    salaryPeriodicityOptions,
    type,
    salaryTypeOptions,
    isTypeRange,
    isValid,
    errors,
    setMinAmount,
    setMaxAmount,
    setGrossSalary,
    setCurrency,
    setWorkplaces,
    setPeriodicity,
    setType,
    buildSalary,
  } = useSalary(initialSalary, validationContext)

  const saveSalary = async () => {
    if (initialSalary) {
      await updateAction(buildSalary(), nodeId)
    } else {
      await createAction(buildSalary(), nodeId)
    }
    onSubmit()
  }

  return (
    <Modal
      illustration={'money-bag'}
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={() => {}}
      footer={
        <Box gap="s16" flexDirection="row" alignItems="center">
          <F0Button
            onClick={onClose}
            label={i18n.t('job_catalog.nodes.salary_modal.cancel')}
            variant="outline"
          />
          <F0Button
            variant="default"
            onClick={saveSalary}
            label={i18n.t('job_catalog.nodes.salary_modal.submit')}
            disabled={!isValid}
          />
        </Box>
      }
    >
      <Box gap="s12" maxWidth="s400">
        <WorkplaceSelector
          selectedWorkplaces={workplaces}
          setSelectedWorkplaces={setWorkplaces}
          disabledWorkplaces={alreadySelectedInOtherSalariesWorkplaces}
          hasAllWorkplacesSalary={hasAllWorkplacesSalary}
          onTotalWorkplacesChange={setTotalWorkplaces}
        />
        <Select
          label={i18n.t('job_catalog.nodes.salary_modal.periodicity')}
          options={salaryPeriodicityOptions}
          value={periodicity}
          onChange={setPeriodicity}
        />
        <CurrencySelector value={currency} onChange={setCurrency} />
        <Select
          label={i18n.t('job_catalog.nodes.salary_modal.pay_structure')}
          options={salaryTypeOptions}
          value={type}
          onChange={setType}
        />
        {isTypeRange ? (
          <Box flexDirection="row" gap="s24">
            <MoneyField
              label={i18n.t('job_catalog.nodes.salary_modal.min_amount')}
              currency={currency}
              value={minAmount}
              onChange={setMinAmount}
              error={errors.minAmount}
              maxDecimals={2}
            />
            <MoneyField
              label={i18n.t('job_catalog.nodes.salary_modal.max_amount')}
              currency={currency}
              value={maxAmount}
              onChange={setMaxAmount}
              error={errors.maxAmount}
              maxDecimals={2}
            />
          </Box>
        ) : (
          <MoneyField
            label={i18n.t('job_catalog.nodes.salary_modal.gross_salary')}
            currency={currency}
            value={grossSalary}
            onChange={setGrossSalary}
            error={errors.grossSalary}
          />
        )}
      </Box>
    </Modal>
  )
}

export default SalaryModal
